import type {
  SearchCriteriaFilterSchema,
  SearchCriteriaTerm,
} from '@iconik-io/iconik-api/src/services/search';

import {
  EXCLUDE_ALL_OF,
  EXCLUDE_ANY_OF,
  INCLUDE_ALL_OF,
  INCLUDE_ANY_OF,
  type MultiSelectFilterGroup,
} from '../../redux/common_filters/types';

/**
 * Groups items by their resolved Elasticsearch term name.
 *
 * Items are grouped into buckets based on their explicit mapping in `itemTermNames`.
 * This allows a single filter group to target multiple distinct fields
 * (e.g., specific nested paths). Items without an entry
 * in `itemTermNames` fall back to `fallbackTermName`.
 */
function groupItemsByTermName(
  items: string[],
  itemTermNames: Record<string, string> | undefined,
  fallbackTermName: string,
): Map<string, string[]> {
  const buckets = new Map<string, string[]>();
  for (const item of items) {
    const name = itemTermNames?.[item] ?? fallbackTermName;
    const bucket = buckets.get(name);
    if (bucket) {
      bucket.push(item);
    } else {
      buckets.set(name, [item]);
    }
  }
  return buckets;
}

export function buildMultiSelectFilterCriteria(
  groups: MultiSelectFilterGroup[],
  termName: string,
): { terms: SearchCriteriaTerm[]; filters: SearchCriteriaFilterSchema[] } {
  const terms: SearchCriteriaTerm[] = [];
  const filters: SearchCriteriaFilterSchema[] = [];

  for (const group of groups) {
    if (group.items.length === 0) {
      continue;
    }

    const buckets = groupItemsByTermName(
      group.items,
      group.itemTermNames,
      termName,
    );

    const additionalTermNames = group.additionalTermNames ?? [];
    const hasAdditionalTerms = additionalTermNames.length > 0;

    switch (group.modifier) {
      case INCLUDE_ALL_OF: {
        // Build individual { name, value } terms for all items across buckets
        const primaryTerms: SearchCriteriaTerm[] = Array.from(
          buckets.entries(),
        ).flatMap(([bucketName, bucketItems]) =>
          bucketItems.map((item) => ({ name: bucketName, value: item })),
        );

        if (hasAdditionalTerms) {
          // Keep nested and flat queries isolated so ES context applies correctly
          const subFilters: SearchCriteriaFilterSchema[] = [
            { operator: 'AND', terms: primaryTerms },
          ];

          for (const additionalName of additionalTermNames) {
            subFilters.push({
              operator: 'AND',
              terms: group.items.map((item) => ({
                name: additionalName,
                value: item,
              })),
            });
          }
          filters.push({ operator: 'OR', filters: subFilters });
        } else {
          terms.push(...primaryTerms);
        }
        break;
      }

      case INCLUDE_ANY_OF: {
        // Build { name, value_in } terms per bucket
        const primaryTerms: SearchCriteriaTerm[] = Array.from(
          buckets.entries(),
        ).map(([bucketName, bucketItems]) => ({
          name: bucketName,
          value_in: bucketItems,
        }));

        if (hasAdditionalTerms) {
          const subFilters: SearchCriteriaFilterSchema[] = [];

          if (primaryTerms.length === 1) {
            subFilters.push({ operator: 'AND', terms: primaryTerms });
          } else {
            subFilters.push({ operator: 'OR', terms: primaryTerms });
          }

          for (const additionalName of additionalTermNames) {
            subFilters.push({
              operator: 'AND',
              terms: [{ name: additionalName, value_in: group.items }],
            });
          }
          filters.push({ operator: 'OR', filters: subFilters });
        } else {
          if (primaryTerms.length === 1) {
            terms.push(...primaryTerms);
          } else {
            filters.push({ operator: 'OR', terms: primaryTerms });
          }
        }
        break;
      }

      case EXCLUDE_ALL_OF: {
        const primaryTerms: SearchCriteriaTerm[] = Array.from(
          buckets.entries(),
        ).flatMap(([bucketName, bucketItems]) =>
          bucketItems.map((item) => ({ name: bucketName, value: item })),
        );

        if (hasAdditionalTerms) {
          // Wrap both primary and additional sub-filters in NOT { OR { ... } }
          const subFilters: SearchCriteriaFilterSchema[] = [
            { operator: 'AND', terms: primaryTerms },
          ];

          for (const additionalName of additionalTermNames) {
            subFilters.push({
              operator: 'AND',
              terms: group.items.map((item) => ({
                name: additionalName,
                value: item,
              })),
            });
          }
          filters.push({
            operator: 'NOT',
            filters: [{ operator: 'OR', filters: subFilters }],
          });
        } else {
          // Only exclude documents matching ALL conditions simultaneously
          filters.push({
            operator: 'NOT',
            filters: [{ operator: 'AND', terms: primaryTerms }],
          });
        }
        break;
      }

      case EXCLUDE_ANY_OF: {
        const primaryTerms: SearchCriteriaTerm[] = Array.from(
          buckets.entries(),
        ).map(([bucketName, bucketItems]) => ({
          name: bucketName,
          value_in: bucketItems,
        }));

        if (hasAdditionalTerms) {
          const subFilters: SearchCriteriaFilterSchema[] = [];

          if (primaryTerms.length === 1) {
            subFilters.push({ operator: 'AND', terms: primaryTerms });
          } else {
            subFilters.push({ operator: 'OR', terms: primaryTerms });
          }

          for (const additionalName of additionalTermNames) {
            subFilters.push({
              operator: 'AND',
              terms: [{ name: additionalName, value_in: group.items }],
            });
          }
          filters.push({
            operator: 'NOT',
            filters: [{ operator: 'OR', filters: subFilters }],
          });
        } else {
          for (const term of primaryTerms) {
            filters.push({
              operator: 'NOT',
              terms: [term],
            });
          }
        }
        break;
      }
    }
  }

  return { terms, filters };
}
